/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import {
    ChangeDetectorRef,
    Component,
    ContentChild, DoCheck,
    ElementRef,
    EventEmitter, HostBinding,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    PLATFORM_ID,
    Renderer2,
    ViewChild
} from '@angular/core';
import {isPlatformServer} from '@angular/common';
import * as tween from '@tweenjs/tween.js';
import {IFCVSPageInfo} from './model/fc-virtual-scroller-page-info.model';
import {IFCVSViewport} from './model/fc-virtual-scroller-viewport.model';
import {
    FCWrapGroupDimension,
    FCWrapGroupDimensions,
    IFCVSDimensions
} from './model/fc-virtual-scroller-dimension.model';
import {FCVirtualScrollerDefaultOptions} from './model/fc-virtual-scroller-default-options.model';

/**
 * Firecrest Virtual Scroller component.
 */
/* eslint-disable */
@Component({
    selector: 'fc-virtual-scroller,[fcVirtualScroller]',
    exportAs: 'fcVirtualScroller',
    host: {
        '[class.horizontal]': 'horizontal',
        '[class.vertical]': '!horizontal',
        '[class.selfScroll]': '!parentScroll',
        '[class.rtl]': 'RTL'
    },
    templateUrl: './fc-virtual-scroller.component.html',
    styleUrls: ['./fc-virtual-scroller.component.scss']
})
export class FcVirtualScrollerComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
    /* eslint-enable */
    /**
     * Declaration for ViewPortItems.
     */
    // @ts-ignore
    public viewPortItems: any[];

    /**
     * JS Window content.
     */
    public window: any = window;

    /**
     * Get Viewport data.
     *
     * @returns IFCVSPageInfo.
     */
    public get viewPortInfo(): IFCVSPageInfo {
        let pageInfo: IFCVSViewport = this.previousViewPort || <any>{};
        return {
            startIndex: pageInfo.startIndex || 0,
            endIndex: pageInfo.endIndex || 0,
            scrollStartPosition: pageInfo.scrollStartPosition || 0,
            scrollEndPosition: pageInfo.scrollEndPosition || 0,
            maxScrollPosition: pageInfo.maxScrollPosition || 0,
            startIndexWithBuffer: pageInfo.startIndexWithBuffer || 0,
            endIndexWithBuffer: pageInfo.endIndexWithBuffer || 0
        } as IFCVSPageInfo;
    }

    /**
     * Input ExecuteRefreshOutsideAngularZone.
     */
    @Input()
    public executeRefreshOutsideAngularZone: boolean = false;

    /**
     * EnableUnequalChildrenSizes.
     */
    protected _enableUnequalChildrenSizes: boolean = false;

    /**
     * Get enableUnequalChildrenSizes.
     *
     * @returns Boolean.
     */
    @Input()
    public get enableUnequalChildrenSizes(): boolean {
        return this._enableUnequalChildrenSizes;
    }

    /**
     * Set enableUnequalChildrenSizes.
     */
    public set enableUnequalChildrenSizes(value: boolean) {
        if (this._enableUnequalChildrenSizes === value) {
            return;
        }

        this._enableUnequalChildrenSizes = value;
        this.minMeasuredChildWidth = undefined;
        this.minMeasuredChildHeight = undefined;
    }

    /**
     * Right To Left direction.
     */
    @Input()
    public RTL: boolean = false;

    /**
     * Input UseMarginInsteadOfTranslate.
     */
    @Input()
    public useMarginInsteadOfTranslate: boolean = false;

    /**
     * Input ModifyOverflowStyleOfParentScroll.
     */
    @Input()
    public modifyOverflowStyleOfParentScroll: boolean;

    /**
     * Input stripedTable.
     */
    @Input()
    public stripedTable: boolean;

    /**
     * Input scrollbarWidth.
     */
    @Input()
    public scrollbarWidth: number | undefined;

    /**
     * Input scrollbarHeight.
     */
    @Input()
    public scrollbarHeight: number | undefined;

    /**
     * Input childWidth.
     */
    @Input()
    // @ts-ignore
    public childWidth: number;

    /**
     *
     */
    @Input()
    // @ts-ignore
    public childHeight: number;

    /**
     * Input ssrChildWidth.
     */
    @Input()
    // @ts-ignore
    public ssrChildWidth: number;

    /**
     * Input ssrChildHeight.
     */
    @Input()
    // @ts-ignore
    public ssrChildHeight: number;

    /**
     * Input ssrViewportWidth with default.
     */
    @Input()
    public ssrViewportWidth: number = 1920;

    /**
     * Input ssrViewportHeight with default.
     */
    @Input()
    public ssrViewportHeight: number = 1080;

    /**
     * _bufferAmount.
     */
    // @ts-ignore
    protected _bufferAmount: number;

    /**
     * Input get bufferAmount.
     *
     * @returns Number.
     */
    @Input()
    public get bufferAmount(): number {
        if (this._bufferAmount >= 0) {
            return this._bufferAmount;
        } else {
            return this.enableUnequalChildrenSizes ? 5 : 0;
        }
    }

    /**
     * Set bufferAmount.
     */
    public set bufferAmount(value: number) {
        this._bufferAmount = value;
    }

    /**
     * Input scrollAnimationTime.
     */
    @Input()
    public scrollAnimationTime: number;

    /**
     * Input resizeBypassRefreshThreshold.
     */
    @Input()
    public resizeBypassRefreshThreshold: number;

    /**
     * Declaration for ScrollThrottlingTime.
     */
    protected _scrollThrottlingTime: number | undefined;

    /**
     * Input scrollThrottlingTime.
     *
     * @returns Any.
     */
    @Input()
    public get scrollThrottlingTime(): any {
        return typeof this._scrollThrottlingTime === 'number'
            ? this._scrollThrottlingTime : undefined;
    }

    /**
     * Set scrollThrottlingTime.
     */
    public set scrollThrottlingTime(value: number) {
        this._scrollThrottlingTime = value;
        this.updateOnScrollFunction();
    }

    /**
     * Declaration for scrollDebounceTime.
     */    
    // @ts-ignore
    protected _scrollDebounceTime: number;

    /**
     * Input scrollDebounceTime.
     *
     * @returns Number.
     */
    @Input()
    public get scrollDebounceTime(): number {
        return this._scrollDebounceTime;
    }

    /**
     * Set scrollDebounceTime.
     */
    public set scrollDebounceTime(value: number) {
        this._scrollDebounceTime = value;
        this.updateOnScrollFunction();
    }

    /**
     * Declaration for OnScroll.
     */
    // @ts-ignore
    protected onScroll: () => void = undefined;

    /**
     * Method to update data of component on scroll.
     */
    protected updateOnScrollFunction(): void {
        if (this.scrollDebounceTime) {
            this.onScroll = <any> this.debounce(() => {
                this.refresh_internal(false);
            }, this.scrollDebounceTime);
        } else if (this.scrollThrottlingTime) {
            this.onScroll = <any> this.throttleTrailing(() => {
                this.refresh_internal(false);
            }, this.scrollThrottlingTime);
        } else {
            this.onScroll = () => {
                this.refresh_internal(false);
            };
        }
    }

    /**
     * Declaration for checkScrollElementResizedTimer.
     */
    protected checkScrollElementResizedTimer: any;

    /**
     * Declaration for _checkResizeInterval.
     */
    protected _checkResizeInterval: any;

    /**
     * Get Check resize Interval.
     *
     * @returns Number.
     */
    @Input()
    public get checkResizeInterval(): number {
        return this._checkResizeInterval;
    }

    /**
     * Set checkResizeInterval.
     */
    public set checkResizeInterval(value: number) {
        if (this._checkResizeInterval === value) {
            return;
        }

        this._checkResizeInterval = value;
        this.addScrollEventHandlers();
    }

    /**
     * Declaration for items.
     */
    protected _items: any[] = [];

    /**
     * Get items.
     *
     * @returns Array of any.
     */
    @Input()
    public get items(): any[] {
        return this._items;
    }

    /**
     * Set items.s.
     */
    public set items(value: any[]) {
        if (value === this._items) {
            return;
        }

        this._items = value || [];
        this.refresh_internal(true);
    }

    /**
     * For Item comparison.
     *
     * @param item1 Any.
     * @param item2 Any.
     * @returns Boolean.
     */
    @Input()
    public compareItems: (item1: any, item2: any) => boolean = (item1: any, item2: any) => item1 === item2;

    /**
     * To detect component direction.
     */
    @HostBinding('class.horizontal') _horizontal: any;

    /**
     * Input Get horizontal.
     *
     * @returns Boolean.
     */
    @Input()
    public get horizontal(): boolean {
        return this._horizontal;
    }

    /**
     * Method to set horizontal.
     */
    public set horizontal(value: boolean) {
        this._horizontal = value;
        this.updateDirection();
    }

    /**
     * Method to revert parent overscroll.
     */
    protected revertParentOverscroll(): void {
        const scrollElement = this.getScrollElement();
        if (scrollElement && this.oldParentScrollOverflow) {
            // @ts-ignore
            scrollElement.style['overflow-y'] = this.oldParentScrollOverflow.y;
            // @ts-ignore
            scrollElement.style['overflow-x'] = this.oldParentScrollOverflow.x;
        }

        this.oldParentScrollOverflow = {x: 0, y: 0};
    }

    /**
     * Get or Set old parent scroll overflow.
     */
    protected oldParentScrollOverflow: any;

    /**
     * Get or Set parent scroll.
     */
    protected _parentScroll: any;

    /**
     * Input get parentScroll.
     *
     * @returns Element | Window.
     */
    @Input()
    public get parentScroll(): Element | Window {
        return this._parentScroll;
    }

    /**
     * Set parentScroll.
     */
    public set parentScroll(value: Element | Window) {
        if (this._parentScroll === value) {
            return;
        }

        this.revertParentOverscroll();
        this._parentScroll = value;
        this.addScrollEventHandlers();

        const scrollElement = this.getScrollElement();
        if (this.modifyOverflowStyleOfParentScroll && scrollElement !== this.element.nativeElement) {
            this.oldParentScrollOverflow = {x: scrollElement.style.overflowX, y: scrollElement.style.overflowY};
            scrollElement.style.overflowX = this.horizontal ? 'visible' : 'auto';
            scrollElement.style.overflowY = this.horizontal ? 'auto' : 'visible';
        }
    }

    /**
     * Output vsUpdate Emitter.
     */
    @Output()
    public vsUpdate: EventEmitter<any[]> = new EventEmitter<any[]>();

    /**
     * Output vsChange Emitter.
     */
    @Output()
    public vsChange: EventEmitter<IFCVSPageInfo> = new EventEmitter<IFCVSPageInfo>();

    /**
     * Output vsStart Emitter.
     */
    @Output()
    public vsStart: EventEmitter<IFCVSPageInfo> = new EventEmitter<IFCVSPageInfo>();

    /**
     * Output vsEnd Emitter.
     */
    @Output()
    public vsEnd: EventEmitter<IFCVSPageInfo> = new EventEmitter<IFCVSPageInfo>();

    /**
     * ViewChild contentElementRef.
     */
    @ViewChild('content', {read: ElementRef, static: true})
    protected contentElementRef: ElementRef | undefined;

    /**
     * ViewChild invisiblePaddingElementRef.
     */
    @ViewChild('invisiblePadding', {read: ElementRef, static: true})
    protected invisiblePaddingElementRef: ElementRef | undefined;

    /**
     * ContentChild headerElementRef.
     */
    @ContentChild('header', {read: ElementRef, static: false})
    protected headerElementRef: ElementRef | undefined;

    /**
     * ContentChild containerElementRef.
     */
    @ContentChild('container', {read: ElementRef, static: false})
    protected containerElementRef: ElementRef | undefined;
    
    /* eslint-disable */
    /**
     * NgOnInit lifecycle.
     */
    public ngOnInit(): void {
        if (this.enableInspectMode) {
            return;
        }
        
        this.addScrollEventHandlers();
    }

    /**
     * NgOnDestroy implementation.
     */
    public ngOnDestroy(): void {
        if (this.enableInspectMode) {
            return;
        }
        
        this.removeScrollEventHandlers();
        this.revertParentOverscroll();
    }

    /**
     * NgOnChanges implementation.
     *
     * @param changes Any.
     */
    public ngOnChanges(changes: any): void {
        if (this.enableInspectMode) {
            if (this.viewPortItems !== this.items) {
                this.viewPortItems = this.items;
            }
            return;
        }
        
        let indexLengthChanged = this.cachedItemsLength !== this.items.length;
        this.cachedItemsLength = this.items.length;

        const firstRun: boolean = !changes.items 
            || !changes.items.previousValue || changes.items.previousValue.length === 0;
        this.refresh_internal(indexLengthChanged || firstRun);
    }

    /**
     * NgDoCheck implementation.
     */
    public ngDoCheck(): void {
        if (this.enableInspectMode) {
            return;
        }
        
        if (this.cachedItemsLength !== this.items.length) {
            this.cachedItemsLength = this.items.length;
            this.refresh_internal(true);
            return;
        }

        if (this.previousViewPort && this.viewPortItems && this.viewPortItems.length > 0) {
            let itemsArrayChanged = false;
            for (let i = 0; i < this.viewPortItems.length; ++i) {
                if (!this.compareItems(this.items[this.previousViewPort.startIndexWithBuffer + i],
                    this.viewPortItems[i])) {
                    itemsArrayChanged = true;
                    break;
                }
            }
            if (itemsArrayChanged) {
                this.refresh_internal(true);
            }
        }
    }

    /* eslint-enable */

    /**
     * Method to perform refresh.
     */
    public refresh(): void {
        this.refresh_internal(true);
    }

    /**
     * Method to invalidate cache measurements.
     */
    public invalidateAllCachedMeasurements(): void {
        this.wrapGroupDimensions = {
            maxChildSizePerWrapGroup: [],
            numberOfKnownWrapGroupChildSizes: 0,
            sumOfKnownWrapGroupChildWidths: 0,
            sumOfKnownWrapGroupChildHeights: 0
        };

        this.minMeasuredChildWidth = undefined;
        this.minMeasuredChildHeight = undefined;

        this.refresh_internal(false);
    }

    /**
     * Method to invalidate cache measurement for item.
     *
     * @param item Any.
     */
    public invalidateCachedMeasurementForItem(item: any): void {
        if (this.enableUnequalChildrenSizes) {
            let index = this.items && this.items.indexOf(item);
            if (index >= 0) {
                this.invalidateCachedMeasurementAtIndex(index);
            }
        } else {
            this.minMeasuredChildWidth = undefined;
            this.minMeasuredChildHeight = undefined;
        }

        this.refresh_internal(false);
    }

    /**
     * Method to invalidate cache measurement at index.
     *
     * @param index Number.
     */
    public invalidateCachedMeasurementAtIndex(index: number): void {
        if (this.enableUnequalChildrenSizes) {
            let cachedMeasurement = this.wrapGroupDimensions.maxChildSizePerWrapGroup[index];
            if (cachedMeasurement) {
                // @ts-ignore
                this.wrapGroupDimensions.maxChildSizePerWrapGroup[index] = undefined;
                --this.wrapGroupDimensions.numberOfKnownWrapGroupChildSizes;
                this.wrapGroupDimensions.sumOfKnownWrapGroupChildWidths -= cachedMeasurement.childWidth || 0;
                this.wrapGroupDimensions.sumOfKnownWrapGroupChildHeights -= cachedMeasurement.childHeight || 0;
            }
        } else {
            this.minMeasuredChildWidth = undefined;
            this.minMeasuredChildHeight = undefined;
        }

        // @ts-ignore
        this.refresh_internal(false);
    }

    /**
     * Method to perform scroll into.
     *
     * @param item Any.
     * @param alignToBeginning Boolean.
     * @param additionalOffset Number.
     * @param animationMilliseconds Number.
     * @param animationCompletedCallback Function.
     */
    // @ts-ignore
    public scrollInto(item: any, alignToBeginning: boolean = true,
        additionalOffset: number = 0,
        animationMilliseconds: number,
        animationCompletedCallback: () => void): void {
        let index: number = this.items.indexOf(item);
        if (index === -1) {
            return;
        }

        this.scrollToIndex(index, alignToBeginning,
            additionalOffset, animationMilliseconds, animationCompletedCallback);
    }

    /**
     * Method to perform scroll to index in public.
     *
     * @param index Number.
     * @param alignToBeginning Boolean.
     * @param additionalOffset Number.
     * @param animationMilliseconds Number.
     * @param animationCompletedCallback Functions.
     */
    public scrollToIndex(index: number, alignToBeginning: boolean = true, additionalOffset: number = 0,
        animationMilliseconds: number,
        animationCompletedCallback: () => void): void {
        let maxRetries: number = 5;

        let retryIfNeeded = () => {
            --maxRetries;
            if (maxRetries <= 0) {
                if (animationCompletedCallback) {
                    animationCompletedCallback();
                }
                return;
            }

            let dimensions = this.calculateDimensions();
            let desiredStartIndex = Math.min(Math.max(index, 0), dimensions.itemCount - 1);
            if (this.previousViewPort.startIndex === desiredStartIndex) {
                if (animationCompletedCallback) {
                    animationCompletedCallback();
                }
                return;
            }

            this.scrollToIndex_internal(index, alignToBeginning,
                additionalOffset, 0, retryIfNeeded);
        };

        this.scrollToIndex_internal(index, alignToBeginning, additionalOffset, animationMilliseconds, retryIfNeeded);
    }

    
    /**
     * Method to scroll to index internally.
     *
     * @param index Number.
     * @param alignToBeginning Boolean.
     * @param additionalOffset Number.
     * @param animationMilliseconds Number.
     * @param animationCompletedCallback Function.
     */
    protected scrollToIndex_internal(index: number, alignToBeginning: boolean = true, additionalOffset: number = 0,
        animationMilliseconds: number,
        animationCompletedCallback: () => void): void {
        animationMilliseconds = animationMilliseconds === undefined ? this.scrollAnimationTime : animationMilliseconds;

        let dimensions = this.calculateDimensions();
        let scroll = this.calculatePadding(index, dimensions) + additionalOffset;
        if (!alignToBeginning) {
            // @ts-ignore
            scroll -= dimensions.wrapGroupsPerPage * dimensions[this._childScrollDim];
        }

        this.scrollToPosition(scroll, animationMilliseconds, animationCompletedCallback);
    }

    /**
     * Method to performs scroll to specific position.
     *
     * @param scrollPosition Number.
     * @param animationMilliseconds Number.
     * @param animationCompletedCallback Function.
     */
    public scrollToPosition(scrollPosition: number,
        // @ts-ignore
        animationMilliseconds: number = undefined,
        // @ts-ignore
        animationCompletedCallback: () => void = undefined): void {
        scrollPosition += this.getElementsOffset();

        animationMilliseconds = animationMilliseconds === undefined ? this.scrollAnimationTime : animationMilliseconds;

        let scrollElement = this.getScrollElement();

        let animationRequest: number;

        if (this.currentTween) {
            this.currentTween.stop();
            this.currentTween = undefined;
        }

        if (!animationMilliseconds) {
            this.renderer.setProperty(scrollElement, this._scrollType, scrollPosition);
            this.refresh_internal(false, animationCompletedCallback);
            return;
        }

        // @ts-ignore
        const tweenConfigObj = {scrollPosition: scrollElement[this._scrollType]};

        let newTween = new tween.Tween(tweenConfigObj)
            .to({scrollPosition}, animationMilliseconds)
            .easing(tween.Easing.Quadratic.Out)
            .onUpdate((data) => {
                if (isNaN(data.scrollPosition)) {
                    return;
                }
                this.renderer.setProperty(scrollElement, this._scrollType, data.scrollPosition);
                this.refresh_internal(false);
            })
            .onStop(() => {
                cancelAnimationFrame(animationRequest);
            })
            .start();

        const animate = (time?: number) => {
            if (!newTween['isPlaying']()) {
                return;
            }

            if (time != null) {
                newTween.update(time);
            }
            if (tweenConfigObj.scrollPosition === scrollPosition) {
                this.refresh_internal(false, animationCompletedCallback);
                return;
            }

            this.zone.runOutsideAngular(() => {
                animationRequest = requestAnimationFrame(animate);
            });
        };

        animate();
        this.currentTween = newTween;
    }

    /**
     * To determine AngularUniversalSSR.
     */
    protected isAngularUniversalSSR: boolean;
    
    private enableInspectMode: boolean = false;

    /**
     * Component constructor.
     *
     * @param element ElementRef.
     * @param renderer Renderer2.
     * @param zone NgZone.
     * @param changeDetectorRef ChangeDetectorRef.
     * @param platformId Object.
     * @param options FCVirtualScrollerDefaultOptions.
     */
    constructor(
        protected readonly element: ElementRef,
        protected readonly renderer: Renderer2,
        protected readonly zone: NgZone,
        protected changeDetectorRef: ChangeDetectorRef,
        @Inject(PLATFORM_ID) platformId: Object,
        @Optional() @Inject('fc-virtual-scroller-default-options')
        options: FCVirtualScrollerDefaultOptions
    ) {
        this.enableInspectMode = this.window.FC.utils.getHashParameter('inspectMode') === 'true';
        if (this.enableInspectMode) {
            return;
        }
        
        this.isAngularUniversalSSR = isPlatformServer(platformId);

        this.checkResizeInterval = options.checkResizeInterval;
        this.modifyOverflowStyleOfParentScroll = options.modifyOverflowStyleOfParentScroll;
        this.resizeBypassRefreshThreshold = options.resizeBypassRefreshThreshold;
        this.scrollAnimationTime = options.scrollAnimationTime;
        this.scrollDebounceTime = options.scrollDebounceTime;
        this.scrollThrottlingTime = options.scrollThrottlingTime;
        this.scrollbarHeight = options.scrollbarHeight;
        this.scrollbarWidth = options.scrollbarWidth;
        this.stripedTable = options.stripedTable;

        this.horizontal = false;
        this.resetWrapGroupDimensions();
    }

    /**
     * Method to getElementSize.
     *
     * @param element HTMLElement.
     * @returns ClientRect.
     */
    protected getElementSize(element: HTMLElement): ClientRect {
        let result = element.getBoundingClientRect();
        let styles = getComputedStyle(element);
        let marginTop = parseInt(styles.marginTop, 10) || 0;
        let marginBottom = parseInt(styles.marginBottom, 10) || 0;
        let marginLeft = parseInt(styles.marginLeft, 10) || 0;
        let marginRight = parseInt(styles.marginRight, 10) || 0;

        return {
            top: result.top + marginTop,
            bottom: result.bottom + marginBottom,
            left: result.left + marginLeft,
            right: result.right + marginRight,
            width: result.width + marginLeft + marginRight,
            height: result.height + marginTop + marginBottom
        };
    }

    /**
     * Method previousScrollBoundingRect.
     *
     * @returns ClientRect | undefined.
     */
    protected previousScrollBoundingRect: ClientRect | undefined;

    /**
     * Method to checkScrollElementResized.
     */
    protected checkScrollElementResized(): void {
        let boundingRect = this.getElementSize(this.getScrollElement());

        let sizeChanged: boolean;
        if (!this.previousScrollBoundingRect) {
            sizeChanged = true;
        } else {
            let widthChange = Math.abs(boundingRect.width - this.previousScrollBoundingRect.width);
            let heightChange = Math.abs(boundingRect.height - this.previousScrollBoundingRect.height);
            sizeChanged = widthChange > this.resizeBypassRefreshThreshold 
                || heightChange > this.resizeBypassRefreshThreshold;
        }

        if (sizeChanged) {
            this.previousScrollBoundingRect = boundingRect;
            if (boundingRect.width > 0 && boundingRect.height > 0) {
                this.refresh_internal(false);
            }
        }
    }

    /**
     * Get or Set padding visibility.
     */
    protected _invisiblePaddingProperty: any;

    /**
     * Get or Set offset type.
     */
    protected _offsetType: any;

    /**
     * Get or Ste scroll type.
     */
    protected _scrollType: any;

    /**
     * Get or Set page offset type.
     */
    protected _pageOffsetType: any;

    /**
     * Get or Set child scroll dimension.
     */
    protected _childScrollDim: any;

    /**
     * Get or Set translate dir.
     */
    protected _translateDir: any;

    /**
     * Get or Set margin dir.
     */
    protected _marginDir: any;

    /**
     * Method to update component direction.
     */
    protected updateDirection(): void {
        if (this.horizontal) {
            this._childScrollDim = 'childWidth';
            this._invisiblePaddingProperty = 'scaleX';
            this._marginDir = 'margin-left';
            this._offsetType = 'offsetLeft';
            this._pageOffsetType = 'pageXOffset';
            this._scrollType = 'scrollLeft';
            this._translateDir = 'translateX';
        } else {
            this._childScrollDim = 'childHeight';
            this._invisiblePaddingProperty = 'scaleY';
            this._marginDir = 'margin-top';
            this._offsetType = 'offsetTop';
            this._pageOffsetType = 'pageYOffset';
            this._scrollType = 'scrollTop';
            this._translateDir = 'translateY';
        }
    }

    /**
     * Method to debounce function execution.
     *
     * @param func Function.
     * @param wait Number.
     * @returns Function.
     */
    protected debounce(func: Function, wait: number): Function {
        const throttled = this.throttleTrailing(func, wait);
        const result = () => {
            // @ts-ignore
            throttled['cancel']();
            throttled.apply(this, arguments);
        };
        result['cancel'] = function () {
            // @ts-ignore
            throttled['cancel']();
        };

        return result;
    }

    /**
     * Method to throttle Trailing.
     *
     * @param func Function.
     * @param wait Number.
     * @returns Function.
     */
    protected throttleTrailing(func: Function, wait: number): Function {
        let timeout: any = undefined;
        let _arguments = arguments;
        const result = () => {
            const _this: any = this;
            _arguments = arguments;

            if (timeout) {
                return;
            }

            if (wait <= 0) {
                func.apply(_this, _arguments);
            } else {
                timeout = setTimeout(function () {
                    timeout = undefined;
                    func.apply(_this, _arguments);
                }, wait);
            }
        };
        result['cancel'] = function () {
            if (timeout) {
                clearTimeout(timeout);
                timeout = undefined;
            }
        };

        return result;
    }

    /**
     * Method to Calculated Scrollbar Width.
     */
    protected calculatedScrollbarWidth: number = 0;

    /**
     * Method to Calculated Scrollbar Height.
     */
    protected calculatedScrollbarHeight: number = 0;

    /**
     * Padding.
     */
    protected padding: number = 0;

    /**
     * PreviousViewPort.
     */
    protected previousViewPort: IFCVSViewport = <any>{};

    /**
     * Get or Set CurrentTween instance.
     */
    protected currentTween: tween.Tween | undefined;

    /**
     * Get or Set cachedItemsLength.
     */
    // @ts-ignores
    protected cachedItemsLength: number;

    /**
     * Get or Set disposeScrollHandler.
     */
    // @ts-ignore
    protected disposeScrollHandler: () => void;

    /**
     * Get or Set disposeResizeHandler.
     */
    // @ts-ignore
    protected disposeResizeHandler: () => void;

    /**
     * Method to perform internal refresh.
     *
     * @param itemsArrayModified Boolean.
     * @param refreshCompletedCallback Function.
     * @param maxRunTimes Number.
     */
    protected refresh_internal(itemsArrayModified: boolean, refreshCompletedCallback?: () => void,
        maxRunTimes?: number): void {
        if (itemsArrayModified && this.previousViewPort && this.previousViewPort.scrollStartPosition > 0) {
            //if items were prepended, scroll forward to keep same items visible
            let oldViewPort = this.previousViewPort;
            let oldViewPortItems = this.viewPortItems;

            let oldRefreshCompletedCallback = refreshCompletedCallback;
            refreshCompletedCallback = () => {
                let scrollLengthDelta = this.previousViewPort.scrollLength - oldViewPort.scrollLength;
                if (scrollLengthDelta > 0 && this.viewPortItems) {
                    let oldStartItem = oldViewPortItems[0];
                    let oldStartItemIndex = this.items.findIndex(x => this.compareItems(oldStartItem, x));
                    if (oldStartItemIndex > this.previousViewPort.startIndexWithBuffer) {
                        let itemOrderChanged = false;
                        for (let i = 1; i < this.viewPortItems.length; ++i) {
                            if (!this.compareItems(this.items[oldStartItemIndex + i], oldViewPortItems[i])) {
                                itemOrderChanged = true;
                                break;
                            }
                        }

                        if (!itemOrderChanged) {
                            this.scrollToPosition(
                                this.previousViewPort.scrollStartPosition + scrollLengthDelta,
                                0, oldRefreshCompletedCallback);
                            return;
                        }
                    }
                }

                if (oldRefreshCompletedCallback) {
                    oldRefreshCompletedCallback();
                }
            };
        }

        this.zone.runOutsideAngular(() => {
            requestAnimationFrame(() => {

                if (itemsArrayModified) {
                    this.resetWrapGroupDimensions();
                }
                let viewport = this.calculateViewport();

                let startChanged = itemsArrayModified || viewport.startIndex !== this.previousViewPort.startIndex;
                let endChanged = itemsArrayModified || viewport.endIndex !== this.previousViewPort.endIndex;
                let scrollLengthChanged = viewport.scrollLength !== this.previousViewPort.scrollLength;
                let paddingChanged = viewport.padding !== this.previousViewPort.padding;
                let scrollPositionChanged = viewport.scrollStartPosition !== this.previousViewPort.scrollStartPosition
                    || viewport.scrollEndPosition !== this.previousViewPort.scrollEndPosition 
                    || viewport.maxScrollPosition !== this.previousViewPort.maxScrollPosition;

                this.previousViewPort = viewport;

                if (scrollLengthChanged) {
                    // @ts-ignores
                    this.renderer.setStyle(this.invisiblePaddingElementRef.nativeElement,
                        'transform', `${this._invisiblePaddingProperty}(${viewport.scrollLength})`);
                    // @ts-ignore
                    this.renderer.setStyle(this.invisiblePaddingElementRef.nativeElement,
                        'webkitTransform',
                        `${this._invisiblePaddingProperty}(${viewport.scrollLength})`);
                }

                if (paddingChanged) {
                    if (this.useMarginInsteadOfTranslate) {
                        // @ts-ignore
                        this.renderer.setStyle(this.contentElementRef.nativeElement,
                            this._marginDir, `${viewport.padding}px`);
                    } else {
                        // @ts-ignore
                        this.renderer.setStyle(this.contentElementRef.nativeElement,
                            'transform', `${this._translateDir}(${viewport.padding}px)`);
                        // @ts-ignore
                        this.renderer.setStyle(this.contentElementRef.nativeElement,
                            'webkitTransform', `${this._translateDir}(${viewport.padding}px)`);
                    }
                }

                if (this.headerElementRef) {
                    // @ts-ignore
                    let scrollPosition = this.getScrollElement()[this._scrollType];
                    let containerOffset = this.getElementsOffset();
                    let offset = Math.max(scrollPosition - viewport.padding - containerOffset
                        + this.headerElementRef.nativeElement.clientHeight, 0);
                    this.renderer.setStyle(this.headerElementRef.nativeElement,
                        'transform', `${this._translateDir}(${offset}px)`);
                    this.renderer.setStyle(this.headerElementRef.nativeElement,
                        'webkitTransform', `${this._translateDir}(${offset}px)`);
                }

                // @ts-ignore
                const changeEventArg: IFCVSPageInfo = (startChanged || endChanged) ? {
                    startIndex: viewport.startIndex,
                    endIndex: viewport.endIndex,
                    scrollStartPosition: viewport.scrollStartPosition,
                    scrollEndPosition: viewport.scrollEndPosition,
                    startIndexWithBuffer: viewport.startIndexWithBuffer,
                    endIndexWithBuffer: viewport.endIndexWithBuffer,
                    maxScrollPosition: viewport.maxScrollPosition
                } : undefined;


                if (startChanged || endChanged || scrollPositionChanged) {
                    const handleChanged = () => {
                        // update the scroll list to trigger re-render of components in viewport
                        this.viewPortItems = viewport.startIndexWithBuffer >= 0
                        && viewport.endIndexWithBuffer >= 0 ? this.items.slice(viewport.startIndexWithBuffer,
                                viewport.endIndexWithBuffer + 1) : [];
                        this.vsUpdate.emit(this.viewPortItems);

                        if (startChanged) {
                            this.vsStart.emit(changeEventArg);
                        }

                        if (endChanged) {
                            this.vsEnd.emit(changeEventArg);
                        }

                        if (startChanged || endChanged) {
                            this.changeDetectorRef.markForCheck();
                            this.vsChange.emit(changeEventArg);
                        }

                        if (maxRunTimes !== undefined && maxRunTimes > 0) {
                            this.refresh_internal(false,
                                refreshCompletedCallback, maxRunTimes - 1);
                            return;
                        }

                        if (refreshCompletedCallback) {
                            refreshCompletedCallback();
                        }
                    };


                    if (this.executeRefreshOutsideAngularZone) {
                        handleChanged();
                    } else {
                        this.zone.run(handleChanged);
                    }
                } else {
                    // @ts-ignore
                    if (maxRunTimes > 0 && (scrollLengthChanged || paddingChanged)) {
                        this.refresh_internal(false,
                            refreshCompletedCallback, 
                            // @ts-ignore
                            maxRunTimes - 1);
                        return;
                    }

                    if (refreshCompletedCallback) {
                        refreshCompletedCallback();
                    }
                }
            });
        });
    }

    /**
     * Get Scroll element.
     *
     * @returns HTMLElement.
     */
    protected getScrollElement(): HTMLElement {
        // @ts-ignore
        return this.parentScroll instanceof Window ? document.scrollingElement
            || document.documentElement || document.body : this.parentScroll || this.element.nativeElement;
    }

    /**
     * Method to  add scroll event handlers.
     */
    protected addScrollEventHandlers(): void {
        if (this.isAngularUniversalSSR) {
            return;
        }

        let scrollElement = this.getScrollElement();

        this.removeScrollEventHandlers();

        this.zone.runOutsideAngular(() => {
            if (this.parentScroll instanceof Window) {
                this.disposeScrollHandler = this.renderer.listen('window',
                    'scroll', this.onScroll);
                this.disposeResizeHandler = this.renderer.listen('window',
                    'resize', this.onScroll);
            } else {
                this.disposeScrollHandler = this.renderer.listen(scrollElement,
                    'scroll', this.onScroll);
                if (this._checkResizeInterval > 0) {
                    this.checkScrollElementResizedTimer = <any>setInterval(() => {
                        this.checkScrollElementResized();
                    }, this._checkResizeInterval);
                }
            }
        });
    }

    /**
     * Method to remove scroll event handlers.
     */
    protected removeScrollEventHandlers(): void {
        if (this.checkScrollElementResizedTimer) {
            clearInterval(this.checkScrollElementResizedTimer);
        }

        if (this.disposeScrollHandler) {
            this.disposeScrollHandler();
            // @ts-ignore
            this.disposeScrollHandler = undefined;
        }

        if (this.disposeResizeHandler) {
            this.disposeResizeHandler();
            // @ts-ignore
            this.disposeResizeHandler = undefined;
        }
    }

    /**
     * Method to get element offset.
     *
     * @returns Number.
     */
    protected getElementsOffset(): number {
        if (this.isAngularUniversalSSR) {
            return 0;
        }

        let offset = 0;

        if (this.containerElementRef && this.containerElementRef.nativeElement) {
            offset += this.containerElementRef.nativeElement[this._offsetType];
        }

        if (this.parentScroll) {
            let scrollElement = this.getScrollElement();
            let elementClientRect = this.getElementSize(this.element.nativeElement);
            let scrollClientRect = this.getElementSize(scrollElement);
            if (this.horizontal) {
                offset += elementClientRect.left - scrollClientRect.left;
            } else {
                offset += elementClientRect.top - scrollClientRect.top;
            }

            if (!(this.parentScroll instanceof Window)) {
                // @ts-ignore
                offset += scrollElement[this._scrollType];
            }
        }

        return offset;
    }

    /**
     * Method to count items per wrap group.
     *
     * @returns Number.
     */
    protected countItemsPerWrapGroup(): number {
        if (this.isAngularUniversalSSR) {
            return Math.round(this.horizontal ?
                this.ssrViewportHeight / this.ssrChildHeight
                : this.ssrViewportWidth / this.ssrChildWidth);
        }

        let propertyName = this.horizontal ? 'offsetLeft' : 'offsetTop';
        let children = ((this.containerElementRef && this.containerElementRef.nativeElement)
            || this.contentElementRef?.nativeElement).children;

        let childrenLength = children ? children.length : 0;
        if (childrenLength === 0) {
            return 1;
        }

        let firstOffset = children[0][propertyName];
        let result = 1;
        while (result < childrenLength && firstOffset === children[result][propertyName]) {
            ++result;
        }

        return result;
    }

    /**
     * Method to get scroll start position.
     *
     * @returns Number.
     */
    protected getScrollStartPosition(): number {
        let windowScrollValue = undefined;
        if (this.parentScroll instanceof Window) {
            windowScrollValue = window[this._pageOffsetType];
        }

        // @ts-ignore
        return windowScrollValue || this.getScrollElement()[this._scrollType] || 0;
    }

    /**
     * Get or Set minMeasuredChildWidth.
     */
    protected minMeasuredChildWidth: number | undefined;

    /**
     * Get or Set minMeasuredChildHeight.
     */
    protected minMeasuredChildHeight: number | undefined;

    /**
     * Get or Set wrapGroupDimensions.
     */
    // @ts-ignore
    protected wrapGroupDimensions: FCWrapGroupDimensions;

    /**
     * Method to reset wrap group dimensions.
     */
    protected resetWrapGroupDimensions(): void {
        const oldWrapGroupDimensions = this.wrapGroupDimensions;
        this.invalidateAllCachedMeasurements();

        if (!this.enableUnequalChildrenSizes || !oldWrapGroupDimensions
            || oldWrapGroupDimensions.numberOfKnownWrapGroupChildSizes === 0) {
            return;
        }

        const itemsPerWrapGroup: number = this.countItemsPerWrapGroup();
        for (let wrapGroupIndex = 0;
            wrapGroupIndex < oldWrapGroupDimensions.maxChildSizePerWrapGroup.length; ++wrapGroupIndex) {
            const oldWrapGroupDimension: FCWrapGroupDimension
                = oldWrapGroupDimensions.maxChildSizePerWrapGroup[wrapGroupIndex];
            if (!oldWrapGroupDimension || !oldWrapGroupDimension.items || !oldWrapGroupDimension.items.length) {
                continue;
            }

            if (oldWrapGroupDimension.items.length !== itemsPerWrapGroup) {
                return;
            }

            let itemsChanged = false;
            let arrayStartIndex = itemsPerWrapGroup * wrapGroupIndex;
            for (let i = 0; i < itemsPerWrapGroup; ++i) {
                if (!this.compareItems(oldWrapGroupDimension.items[i], this.items[arrayStartIndex + i])) {
                    itemsChanged = true;
                    break;
                }
            }

            if (!itemsChanged) {
                ++this.wrapGroupDimensions.numberOfKnownWrapGroupChildSizes;
                this.wrapGroupDimensions.sumOfKnownWrapGroupChildWidths += oldWrapGroupDimension.childWidth || 0;
                this.wrapGroupDimensions.sumOfKnownWrapGroupChildHeights += oldWrapGroupDimension.childHeight || 0;
                this.wrapGroupDimensions.maxChildSizePerWrapGroup[wrapGroupIndex] = oldWrapGroupDimension;
            }
        }
    }

    /**
     * Method to calculate dimensions.
     *
     * @returns IFCVSDimensions.
     */
    protected calculateDimensions(): IFCVSDimensions {
        let scrollElement = this.getScrollElement();

        const maxCalculatedScrollBarSize: number = 25;
        this.calculatedScrollbarHeight = Math.max(
            Math.min(scrollElement.offsetHeight - scrollElement.clientHeight, maxCalculatedScrollBarSize),
            this.calculatedScrollbarHeight);
        this.calculatedScrollbarWidth = Math.max(
            Math.min(scrollElement.offsetWidth - scrollElement.clientWidth,
                maxCalculatedScrollBarSize), this.calculatedScrollbarWidth);

        let viewportWidth = scrollElement.offsetWidth - (this.scrollbarWidth
            || this.calculatedScrollbarWidth || (this.horizontal ? 0 : maxCalculatedScrollBarSize));
        let viewportHeight = scrollElement.offsetHeight - (this.scrollbarHeight
            || this.calculatedScrollbarHeight || (this.horizontal ? maxCalculatedScrollBarSize : 0));

        let content = (this.containerElementRef && this.containerElementRef.nativeElement)
            || this.contentElementRef?.nativeElement;

        let itemsPerWrapGroup = this.countItemsPerWrapGroup();
        let wrapGroupsPerPage;

        let defaultChildWidth: number;
        let defaultChildHeight: number;

        if (this.isAngularUniversalSSR) {
            viewportWidth = this.ssrViewportWidth;
            viewportHeight = this.ssrViewportHeight;
            defaultChildWidth = this.ssrChildWidth;
            defaultChildHeight = this.ssrChildHeight;
            let itemsPerRow = Math.max(Math.ceil(viewportWidth / defaultChildWidth), 1);
            let itemsPerCol = Math.max(Math.ceil(viewportHeight / defaultChildHeight), 1);
            wrapGroupsPerPage = this.horizontal ? itemsPerRow : itemsPerCol;
        } else if (!this.enableUnequalChildrenSizes) {
            if (content.children.length > 0) {
                if (!this.childWidth || !this.childHeight) {
                    if (!this.minMeasuredChildWidth && viewportWidth > 0) {
                        this.minMeasuredChildWidth = viewportWidth;
                    }
                    if (!this.minMeasuredChildHeight && viewportHeight > 0) {
                        this.minMeasuredChildHeight = viewportHeight;
                    }
                }

                let child = content.children[0];
                let clientRect = this.getElementSize(child);
                if (typeof this.minMeasuredChildWidth === 'number') {
                    this.minMeasuredChildWidth = Math.min(this.minMeasuredChildWidth, clientRect.width);
                }
                if (typeof this.minMeasuredChildHeight === 'number') {
                    this.minMeasuredChildHeight = Math.min(this.minMeasuredChildHeight, clientRect.height);
                }
            }

            defaultChildWidth = this.childWidth || this.minMeasuredChildWidth || viewportWidth;
            defaultChildHeight = this.childHeight || this.minMeasuredChildHeight || viewportHeight;
            let itemsPerRow = Math.max(Math.ceil(viewportWidth / defaultChildWidth), 1);
            let itemsPerCol = Math.max(Math.ceil(viewportHeight / defaultChildHeight), 1);
            wrapGroupsPerPage = this.horizontal ? itemsPerRow : itemsPerCol;
        } else {
            // @ts-ignore
            let scrollOffset = scrollElement[this._scrollType] - (this.previousViewPort ?
                this.previousViewPort.padding : 0);

            let arrayStartIndex = this.previousViewPort.startIndexWithBuffer || 0;
            let wrapGroupIndex = Math.ceil(arrayStartIndex / itemsPerWrapGroup);

            let maxWidthForWrapGroup = 0;
            let maxHeightForWrapGroup = 0;
            let maxHWForWrapGroup = 0;
            let sumOfVisibleMaxWidths = 0;
            let sumOfVisibleMaxHeights = 0;
            let sumOfVisibleMaxHW = 0;
            wrapGroupsPerPage = 0;

            for (let i = 0; i < content.children.length; ++i) {
                ++arrayStartIndex;
                let child = content.children[i];
                let clientRect = this.getElementSize(child);

                maxWidthForWrapGroup = Math.max(maxWidthForWrapGroup, clientRect.width);
                maxHeightForWrapGroup = Math.max(maxHeightForWrapGroup, clientRect.height);

                if (arrayStartIndex % itemsPerWrapGroup === 0) {
                    let oldValue = this.wrapGroupDimensions.maxChildSizePerWrapGroup[wrapGroupIndex];
                    if (oldValue) {
                        --this.wrapGroupDimensions.numberOfKnownWrapGroupChildSizes;
                        this.wrapGroupDimensions.sumOfKnownWrapGroupChildWidths -= oldValue.childWidth || 0;
                        this.wrapGroupDimensions.sumOfKnownWrapGroupChildHeights -= oldValue.childHeight || 0;
                    }

                    ++this.wrapGroupDimensions.numberOfKnownWrapGroupChildSizes;
                    const items = this.items.slice(arrayStartIndex - itemsPerWrapGroup, arrayStartIndex);
                    this.wrapGroupDimensions.maxChildSizePerWrapGroup[wrapGroupIndex] = {
                        childWidth: maxWidthForWrapGroup,
                        childHeight: maxHeightForWrapGroup,
                        items: items
                    };
                    this.wrapGroupDimensions.sumOfKnownWrapGroupChildWidths += maxWidthForWrapGroup;
                    this.wrapGroupDimensions.sumOfKnownWrapGroupChildHeights += maxHeightForWrapGroup;

                    if (this.horizontal) {
                        maxHWForWrapGroup  = maxWidthForWrapGroup;
                        sumOfVisibleMaxHW = sumOfVisibleMaxWidths;
                    } else {
                        maxHWForWrapGroup  = maxHeightForWrapGroup;
                        sumOfVisibleMaxHW = sumOfVisibleMaxHeights;
                    }

                    let maxVisibleHWForWrapGroup = Math.min(
                        maxHWForWrapGroup,
                        Math.max(viewportHeight - sumOfVisibleMaxHW, 0));
                    if (scrollOffset > 0) {
                        let scrollOffsetToRemove = Math.min(scrollOffset, maxVisibleHWForWrapGroup);
                        maxVisibleHWForWrapGroup -= scrollOffsetToRemove;
                        scrollOffset -= scrollOffsetToRemove;
                    }

                    sumOfVisibleMaxHW += maxVisibleHWForWrapGroup;
                    if (maxVisibleHWForWrapGroup > 0 && viewportHeight >= sumOfVisibleMaxHW) {
                        ++wrapGroupsPerPage;
                    }

                    ++wrapGroupIndex;

                    maxWidthForWrapGroup = 0;
                    maxHeightForWrapGroup = 0;
                    maxHWForWrapGroup = 0;
                }
            }

            let averageChildWidth = this.wrapGroupDimensions.sumOfKnownWrapGroupChildWidths
                / this.wrapGroupDimensions.numberOfKnownWrapGroupChildSizes;
            let averageChildHeight = this.wrapGroupDimensions.sumOfKnownWrapGroupChildHeights
                / this.wrapGroupDimensions.numberOfKnownWrapGroupChildSizes;
            defaultChildWidth = this.childWidth || averageChildWidth || viewportWidth;
            defaultChildHeight = this.childHeight || averageChildHeight || viewportHeight;

            if (this.horizontal) {
                if (viewportWidth > sumOfVisibleMaxWidths) {
                    wrapGroupsPerPage += Math.ceil((viewportWidth - sumOfVisibleMaxWidths) / defaultChildWidth);
                }
            } else {
                if (viewportHeight > sumOfVisibleMaxHeights) {
                    wrapGroupsPerPage += Math.ceil((viewportHeight - sumOfVisibleMaxHeights) / defaultChildHeight);
                }
            }
        }

        let itemCount = this.items.length;
        let itemsPerPage = itemsPerWrapGroup * wrapGroupsPerPage;
        let pageCount_fractional = itemCount / itemsPerPage;
        let numberOfWrapGroups = Math.ceil(itemCount / itemsPerWrapGroup);
        let defaultScrollLengthPerWrapGroup = this.horizontal
            ? defaultChildWidth : defaultChildHeight;
        let scrollLength;
        if (this.enableUnequalChildrenSizes) {
            scrollLength = this.internalCalculatePadding(numberOfWrapGroups,
                defaultScrollLengthPerWrapGroup);
        } else {
            scrollLength = numberOfWrapGroups * defaultScrollLengthPerWrapGroup;
        }

        if (this.headerElementRef) {
            scrollLength += this.headerElementRef.nativeElement.clientHeight;
        }

        let viewportLength = this.horizontal ? viewportWidth : viewportHeight;
        let maxScrollPosition = Math.max(scrollLength - viewportLength, 0);

        return {
            childHeight: defaultChildHeight,
            childWidth: defaultChildWidth,
            itemCount: itemCount,
            itemsPerPage: itemsPerPage,
            itemsPerWrapGroup: itemsPerWrapGroup,
            maxScrollPosition: maxScrollPosition,
            pageCount_fractional: pageCount_fractional,
            scrollLength: scrollLength,
            viewportLength: viewportLength,
            wrapGroupsPerPage: wrapGroupsPerPage
        };
    }

    /**
     * Get or Set cached page size.
     */
    protected cachedPageSize: number = 0;

    /**
     * Get or Set previous scroll number element.
     */
    protected previousScrollNumberElements: number = 0;

    /**
     * Method to calculate padding.
     *
     * @param arrayStartIndexWithBuffer Number.
     * @param dimensions IFCVSDimensions.
     * @returns Number.
     */
    protected calculatePadding(arrayStartIndexWithBuffer: number, dimensions: IFCVSDimensions): number {
        if (dimensions.itemCount === 0) {
            return 0;
        }

        // @ts-ignore
        let defaultScrollLengthPerWrapGroup = dimensions[this._childScrollDim];
        let startingWrapGroupIndex = Math.floor(arrayStartIndexWithBuffer / dimensions.itemsPerWrapGroup) || 0;

        if (!this.enableUnequalChildrenSizes) {
            return defaultScrollLengthPerWrapGroup * startingWrapGroupIndex;
        }

        return this.internalCalculatePadding(startingWrapGroupIndex, defaultScrollLengthPerWrapGroup);
    }
    
    private internalCalculatePadding(items: number, defaultScrollLength: number): number {
        let tmp = 0, numUnknownChildSizes = 0;
        for (let i = 0; i < items; ++i) {
            let childSize = this.wrapGroupDimensions.maxChildSizePerWrapGroup[i]
                // @ts-ignore
                && this.wrapGroupDimensions.maxChildSizePerWrapGroup[i][this._childScrollDim];
            if (childSize) {
                tmp += childSize;
            } else {
                ++numUnknownChildSizes;
            }
        }
        tmp += Math.round(numUnknownChildSizes * defaultScrollLength);
        return tmp;
    }

    /**
     * Method to calculate page info.
     *
     * @param scrollPosition Number.
     * @param dimensions IFCVSDimensions.
     * @returns IFCVSDimensions.
     */
    protected calculatePageInfo(scrollPosition: number, dimensions: IFCVSDimensions): IFCVSPageInfo {
        let scrollPercentage = 0;
        if (this.enableUnequalChildrenSizes) {
            const numberOfWrapGroups = Math.ceil(dimensions.itemCount / dimensions.itemsPerWrapGroup);
            let totalScrolledLength = 0;
            // @ts-ignore
            let defaultScrollLengthPerWrapGroup = dimensions[this._childScrollDim];
            for (let i = 0; i < numberOfWrapGroups; ++i) {
                let childSize = this.wrapGroupDimensions.maxChildSizePerWrapGroup[i]
                    // @ts-ignore
                    && this.wrapGroupDimensions.maxChildSizePerWrapGroup[i][this._childScrollDim];
                if (childSize) {
                    totalScrolledLength += childSize;
                } else {
                    totalScrolledLength += defaultScrollLengthPerWrapGroup;
                }

                if (scrollPosition < totalScrolledLength) {
                    scrollPercentage = i / numberOfWrapGroups;
                    break;
                }
            }
        } else {
            scrollPercentage = scrollPosition / dimensions.scrollLength;
        }

        let startingArrayIndex_fractional = Math.min(
            Math.max(scrollPercentage * dimensions.pageCount_fractional, 0), dimensions.pageCount_fractional)
            * dimensions.itemsPerPage;

        let maxStart = dimensions.itemCount - dimensions.itemsPerPage - 1;
        let arrayStartIndex = Math.min(Math.floor(startingArrayIndex_fractional), maxStart);
        arrayStartIndex -= arrayStartIndex % dimensions.itemsPerWrapGroup; // round down to start of wrapGroup

        if (this.stripedTable) {
            let bufferBoundary = 2 * dimensions.itemsPerWrapGroup;
            if (arrayStartIndex % bufferBoundary !== 0) {
                arrayStartIndex = Math.max(arrayStartIndex - arrayStartIndex % bufferBoundary, 0);
            }
        }

        let arrayEndIndex = Math.ceil(startingArrayIndex_fractional) + dimensions.itemsPerPage - 1;
        let endIndexWithinWrapGroup = (arrayEndIndex + 1) % dimensions.itemsPerWrapGroup;
        if (endIndexWithinWrapGroup > 0) {
            arrayEndIndex += dimensions.itemsPerWrapGroup - endIndexWithinWrapGroup; // round up to end of wrapGroup
        }

        if (isNaN(arrayStartIndex)) {
            arrayStartIndex = 0;
        }
        if (isNaN(arrayEndIndex)) {
            arrayEndIndex = 0;
        }

        arrayStartIndex = Math.min(Math.max(arrayStartIndex, 0), dimensions.itemCount - 1);
        arrayEndIndex = Math.min(Math.max(arrayEndIndex, 0), dimensions.itemCount - 1);

        let bufferSize = this.bufferAmount * dimensions.itemsPerWrapGroup;
        let startIndexWithBuffer = Math.min(
            Math.max(arrayStartIndex - bufferSize, 0), dimensions.itemCount - 1);
        let endIndexWithBuffer = Math.min(Math.max(arrayEndIndex + bufferSize, 0), dimensions.itemCount - 1);

        return {
            startIndex: arrayStartIndex,
            endIndex: arrayEndIndex,
            startIndexWithBuffer: startIndexWithBuffer,
            endIndexWithBuffer: endIndexWithBuffer,
            scrollStartPosition: scrollPosition,
            scrollEndPosition: scrollPosition + dimensions.viewportLength,
            maxScrollPosition: dimensions.maxScrollPosition
        };
    }

    /**
     * Method to calculate viewport.
     *
     * @returns IFCVSViewport.
     */
    protected calculateViewport(): IFCVSViewport {
        let dimensions = this.calculateDimensions();
        let offset = this.getElementsOffset();

        let scrollStartPosition = this.getScrollStartPosition();
        if (scrollStartPosition > (dimensions.scrollLength + offset) && !(this.parentScroll instanceof Window)) {
            scrollStartPosition = dimensions.scrollLength;
        } else {
            scrollStartPosition -= offset;
        }
        scrollStartPosition = Math.max(0, scrollStartPosition);

        let pageInfo = this.calculatePageInfo(scrollStartPosition, dimensions);
        let newPadding = this.calculatePadding(pageInfo.startIndexWithBuffer, dimensions);
        let newScrollLength = dimensions.scrollLength;

        return {
            startIndex: pageInfo.startIndex,
            endIndex: pageInfo.endIndex,
            startIndexWithBuffer: pageInfo.startIndexWithBuffer,
            endIndexWithBuffer: pageInfo.endIndexWithBuffer,
            padding: Math.round(newPadding),
            scrollLength: Math.round(newScrollLength),
            scrollStartPosition: pageInfo.scrollStartPosition,
            scrollEndPosition: pageInfo.scrollEndPosition,
            maxScrollPosition: pageInfo.maxScrollPosition
        };
    }
}
